import {
    ProjectWorkEmployee,
    ProjectWorkHourlyCode,
    ProjectWorkHourlyLine,
    ProjectWorkListRow,
    ProjectWorkMiscLine,
    ProjectWorkPieceLine,
    ProjectWorkPunch,
} from "../interfaces/ProjectWork";

/*
 * Pure filtering for the project list and the project-detail view on Project Work.
 *
 * Same matching rules as the Projects page: lowercase, trimmed, plain substring over one
 * flat searchable string per row, fields joined by a separator so a term can't bridge two
 * of them. Nothing here touches a total the page reports — see `EmployeeScope` for the one
 * deliberate exception, the punch table's own scope note.
 */

const SEP = " · ";

const searchable = (...parts: (string | null | undefined)[]): string =>
    parts.filter((p) => !!p).join(SEP).toLowerCase();

export const normalizeTerm = (term: string): string => term.trim().toLowerCase();

/** Scope chips. A scope, not a search: it cuts rows, sub-sections and punches at once. */
export type EmployeeScope = "all" | "hourly" | "piece";

// ── Project list ────────────────────────────────────────────────────────────

export interface ProjectListFilterResult {
    projects: ProjectWorkListRow[];
    matches: number;
}

/** `term` must already be normalized. Matches project # or name; the week's KPIs are never filtered. */
export const filterProjectList = (
    projects: ProjectWorkListRow[],
    term: string
): ProjectListFilterResult => {
    if (!term) return { projects, matches: 0 };
    const kept = projects.filter((p) => searchable(p.altProjectID, p.name).includes(term));
    return { projects: kept, matches: kept.length };
};

// ── Labor by Cost Code (flat rollup table) ──────────────────────────────────

export interface CodeRollupFilterResult {
    codes: ProjectWorkHourlyCode[];
    matches: number;
}

/** `term` must already be normalized. The `Total hourly labor` footer is never filtered. */
export const filterCodeRollup = (
    codes: ProjectWorkHourlyCode[],
    term: string
): CodeRollupFilterResult => {
    if (!term) return { codes, matches: 0 };
    const kept = codes.filter((c) => searchable(c.code, c.description).includes(term));
    return { codes: kept, matches: kept.length };
};

// ── Employees accordion ─────────────────────────────────────────────────────

export interface FilteredEmployee {
    emp: ProjectWorkEmployee;
    /** Name or ID matched: the row shows whole and untrimmed. */
    direct: boolean;
    /** Only a line deep inside matched: the row auto-expands and its tables are trimmed. */
    deepOnly: boolean;
    hourlyLines: ProjectWorkHourlyLine[];
    pieceTasks: ProjectWorkPieceLine[];
    miscTasks: ProjectWorkMiscLine[];
    /** Chip-scoped punches. Never trimmed by the text term — they are the time record. */
    punches: ProjectWorkPunch[];
    /** Sub-section visibility, decided by the chip alone. */
    showHourly: boolean;
    showPiece: boolean;
}

export interface EmployeesFilterResult {
    rows: FilteredEmployee[];
    matches: number;
    /** Employees whose row must be open because the hit is inside it. */
    revealIDs: number[];
}

const hourlyLineText = (l: ProjectWorkHourlyLine) => searchable(l.code, l.description);

/** Lot path reads as a path in the UI; flatten the separators so a term can span it. */
const pieceLineText = (p: ProjectWorkPieceLine) =>
    searchable(p.code, p.taskName, (p.intermediateName ?? "").replace(/>/g, " "));

const miscLineText = (m: ProjectWorkMiscLine) =>
    searchable(m.miscDescription, (m.intermediateName ?? "").replace(/>/g, " "));

const empText = (e: ProjectWorkEmployee) =>
    searchable(e.name, e.altEmployeeID, e.supervisorName);

const hasPieceWork = (e: ProjectWorkEmployee) =>
    (e.pieceTasks?.length ?? 0) > 0 || (e.miscTasks?.length ?? 0) > 0;

/**
 * Punch rows in scope for a chip. Meals stay in both scopes — an unpaid break is context
 * for the day, not a kind of work. Drive punches never reach this page at all.
 */
export const scopePunches = (
    punches: ProjectWorkPunch[],
    scope: EmployeeScope
): ProjectWorkPunch[] => {
    if (scope === "all") return punches;
    return punches.filter((p) => p.type === "meal" || p.type === scope);
};

/** Inline note appended to the punch subtitle so a reduced total can't be misread. */
export const punchScopeNote = (scope: EmployeeScope): string | undefined =>
    scope === "all" ? undefined : `${scope} punches only`;

/** `term` must already be normalized. The chip narrows first, then the term filters within it. */
export const filterEmployees = (
    employees: ProjectWorkEmployee[],
    term: string,
    scope: EmployeeScope
): EmployeesFilterResult => {
    const out: EmployeesFilterResult = { rows: [], matches: 0, revealIDs: [] };

    employees.forEach((emp) => {
        // 1. Chip: drop the employee outright, and take the out-of-scope sections off the table.
        if (scope === "hourly" && !(emp.hourlyMinutes > 0)) return;
        if (scope === "piece" && !hasPieceWork(emp)) return;
        const showHourly = scope !== "piece";
        const showPiece = scope !== "hourly";
        const scopedHourly = showHourly ? emp.hourlyLines ?? [] : [];
        const scopedPiece = showPiece ? emp.pieceTasks ?? [] : [];
        const scopedMisc = showPiece ? emp.miscTasks ?? [] : [];
        const punches = scopePunches(emp.punches ?? [], scope);

        const row: FilteredEmployee = {
            emp,
            direct: false,
            deepOnly: false,
            hourlyLines: scopedHourly,
            pieceTasks: scopedPiece,
            miscTasks: scopedMisc,
            punches,
            showHourly,
            showPiece,
        };

        if (!term) {
            out.rows.push(row);
            out.matches += 1;
            return;
        }

        // 2. Searching a person means you want everything about that person.
        if (empText(emp).includes(term)) {
            out.rows.push({ ...row, direct: true });
            out.matches += 1;
            return;
        }

        // 3. Deep match: keep only the lines that hit, and open the row to show them.
        const hourlyLines = scopedHourly.filter((l) => hourlyLineText(l).includes(term));
        const pieceTasks = scopedPiece.filter((p) => pieceLineText(p).includes(term));
        const miscTasks = scopedMisc.filter((m) => miscLineText(m).includes(term));
        if (hourlyLines.length === 0 && pieceTasks.length === 0 && miscTasks.length === 0) return;

        out.revealIDs.push(emp.employeeID);
        out.matches += 1;
        out.rows.push({ ...row, deepOnly: true, hourlyLines, pieceTasks, miscTasks });
    });

    return out;
};
